/**
 * useTalkSession
 * SSE-based hook for the Talk with Martha chat panel.
 * Supports session persistence: load previous sessions, start new ones, archive.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { talkSessionsApi, type TalkSessionInfo, type TalkSessionMessage } from '../api/talkSessions';
import { derivePageContext as buildRichPageContext } from '../api/topics';

export interface TalkToolCall {
  name: string;
  status: 'running' | 'done';
  summary?: string;
  /** Parsed agent short name if tool name starts with `agent_` */
  agent?: string;
  /** Human-readable operation name (underscores replaced with spaces) */
  displayName?: string;
}

/**
 * Parse an agent tool name into agent + operation parts.
 * e.g. `agent_content_generate_content` → { agent: 'content', operation: 'generate content' }
 */
function parseAgentTool(name: string): { agent: string; operation: string } | null {
  const match = name.match(/^agent_([^_]+)_(.+)$/);
  if (!match) return null;
  return { agent: match[1], operation: match[2].replace(/_/g, ' ') };
}

export interface TalkMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  toolCalls?: TalkToolCall[];
  isStreaming?: boolean;
  /** Agent mode metadata — populated when agentMode=true */
  agentMode?: boolean;
  statusMessage?: string;
  thinkingText?: string;
  progress?: { step: number; total: number; description: string };
}

export interface SendMessageOptions {
  agentMode?: boolean;
  /** Extra fields merged into pageContext (e.g. document_id from topic canvas) */
  pageContextOverride?: Record<string, unknown>;
}

export interface UseTalkSessionResult {
  messages: TalkMessage[];
  isStreaming: boolean;
  sendMessage: (content: string, options?: SendMessageOptions) => void;
  cancelStream: () => void;
  sessionId: string | null;
  sessions: TalkSessionInfo[];
  loadSession: (sessionId: string) => void;
  startNewSession: () => void;
  archiveSession: (sessionId: string) => void;
  refreshSessions: () => void;
  isLoadingSessions: boolean;
  /** Queued message awaiting send after current stream completes */
  queuedMessage: string | null;
  queueMessage: (content: string) => void;
  clearQueue: () => void;
}

const generateId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const WELCOME_MESSAGE: TalkMessage = {
  id: 'welcome',
  role: 'system',
  content: "Hi! I'm Martha. Ask me about your project, sprint status, or anything you need help with.",
  timestamp: new Date(),
};

/**
 * Derive full page context from the current URL.
 * Merges the rich context (url, pane, project_id, document_id, design_context, etc.)
 * with entity-level type+id for backend tool scoping.
 */
function derivePageContext(): Record<string, unknown> | undefined {
  const rich = buildRichPageContext();
  const path = window.location.pathname;

  // Entity-level context — backend uses type+id to load sprint/doc/epic/board content
  const sprintMatch = path.match(/\/pr\/[^/]+\/sp\/([^/]+)/);
  if (sprintMatch) return { ...rich, type: 'sprint', id: sprintMatch[1] };

  const epicMatch = path.match(/\/pr\/[^/]+\/ep\/([^/]+)/);
  if (epicMatch) return { ...rich, type: 'epic', id: epicMatch[1] };

  const docMatch = path.match(/\/pr\/[^/]+\/do\/([^/]+)/);
  if (docMatch) return { ...rich, type: 'document', id: docMatch[1] };

  const boardMatch = path.match(/\/pr\/[^/]+\/bo\/([^/]+)/);
  if (boardMatch) return { ...rich, type: 'board', id: boardMatch[1] };

  // ADL Playground page — pass page ID so Martha gets ADL component context
  const playgroundMatch = path.match(/\/ar\/pg\/([^/]+)/);
  if (playgroundMatch) return { ...rich, type: 'playground', id: playgroundMatch[1], page_code: 'pg' };

  // Non-project pages (Archie pane, Support, Sales etc.) — pass rich context only
  return rich ?? undefined;
}

/**
 * Parse an SSE chunk buffer and extract complete events.
 * Returns [parsedEvents, remainingBuffer].
 */
function parseSseChunk(buffer: string): [Array<{ event: string; data: string }>, string] {
  const events: Array<{ event: string; data: string }> = [];
  const lines = buffer.split('\n');
  let currentEvent = '';
  let currentData = '';
  let consumed = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check if this is the last line and might be incomplete (no trailing \n)
    if (i === lines.length - 1 && !buffer.endsWith('\n')) {
      break;
    }

    consumed += line.length + 1; // +1 for the \n

    if (line.startsWith('event: ')) {
      currentEvent = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      currentData = line.slice(6);
    } else if (line === '') {
      // Empty line = end of event
      if (currentEvent && currentData) {
        events.push({ event: currentEvent, data: currentData });
      }
      currentEvent = '';
      currentData = '';
    }
  }

  return [events, buffer.slice(consumed)];
}

/**
 * Extract readable text from Anthropic MessageParam content.
 * Handles string, array of text blocks, and tool_use blocks.
 */
function extractTextContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    const parts: string[] = [];
    for (const block of content) {
      if (block && typeof block === 'object') {
        if ('text' in block && typeof (block as { text: unknown }).text === 'string') {
          parts.push((block as { text: string }).text);
        } else if ('type' in block && (block as { type: string }).type === 'tool_use') {
          parts.push(`[Tool: ${(block as { name?: string }).name ?? 'unknown'}]`);
        }
      }
    }
    return parts.join('\n');
  }
  return String(content ?? '');
}

export function useTalkSession(projectId: string | null): UseTalkSessionResult {
  const [messages, setMessages] = useState<TalkMessage[]>([WELCOME_MESSAGE]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionIdRaw] = useState<string | null>(() => {
    try { return localStorage.getItem('martha_talk_session_id'); } catch { return null; }
  });
  const setSessionId = useCallback((id: string | null) => {
    setSessionIdRaw(id);
    try {
      if (id) localStorage.setItem('martha_talk_session_id', id);
      else localStorage.removeItem('martha_talk_session_id');
    } catch { /* ignore */ }
  }, []);
  const [sessions, setSessions] = useState<TalkSessionInfo[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const sessionRef = useRef<string | null>(null);
  const queuedRef = useRef<string | null>(null);

  // Keep refs in sync
  sessionRef.current = sessionId;
  queuedRef.current = queuedMessage;

  const cancelStream = useCallback(() => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    abortRef.current = null;
    setMessages((prev) =>
      prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
    );
    setIsStreaming(false);
  }, []);

  const queueMessage = useCallback((content: string) => {
    queuedRef.current = content;
    setQueuedMessage(content);
  }, []);

  const clearQueue = useCallback(() => {
    queuedRef.current = null;
    setQueuedMessage(null);
  }, []);

  // ── Session list management ─────────────────────────────────────────────

  const refreshSessions = useCallback(async () => {
    setIsLoadingSessions(true);
    try {
      const list = await talkSessionsApi.list();
      setSessions(list);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  // Load session list on mount
  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const loadSession = useCallback(async (targetSessionId: string) => {
    try {
      const msgs = await talkSessionsApi.getMessages(targetSessionId);
      const loaded: TalkMessage[] = [WELCOME_MESSAGE];
      for (const msg of msgs) {
        loaded.push({
          id: `db_${msg.id}`,
          role: msg.role,
          content: extractTextContent(msg.content),
          timestamp: new Date(msg.created_at),
        });
      }
      setMessages(loaded);
      setSessionId(targetSessionId);
    } catch (err) {
      console.error('Failed to load session:', err);
    }
  }, [setSessionId]);

  const startNewSession = useCallback(() => {
    // Abort any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setMessages([WELCOME_MESSAGE]);
    setSessionId(null);
    setIsStreaming(false);
  }, [setSessionId]);

  const archiveSession = useCallback(async (targetSessionId: string) => {
    try {
      await talkSessionsApi.archive(targetSessionId);
      setSessions((prev) => prev.filter((s) => s.id !== targetSessionId));
      // If archiving the current session, start a new one
      if (sessionRef.current === targetSessionId) {
        startNewSession();
      }
    } catch (err) {
      console.error('Failed to archive session:', err);
    }
  }, [startNewSession]);

  // ── Send message (SSE streaming) ────────────────────────────────────────

  const sendMessage = useCallback(
    (content: string, options?: SendMessageOptions) => {
      if (!content.trim()) return;

      // Abort any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      const agentMode = options?.agentMode ?? false;

      const userMsg: TalkMessage = {
        id: generateId(),
        role: 'user',
        content,
        timestamp: new Date(),
      };

      const assistantId = generateId();
      const assistantMsg: TalkMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        toolCalls: [],
        isStreaming: true,
        agentMode,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      const pageContext = options?.pageContextOverride
        ? { ...derivePageContext(), ...options.pageContextOverride }
        : derivePageContext();

      const body: Record<string, unknown> = {
        query: content,
        stream: true,
      };
      if (sessionRef.current) body.sessionId = sessionRef.current;
      if (projectId) body.projectId = projectId;
      if (pageContext) body.pageContext = pageContext;
      if (agentMode) body.agentMode = true;

      fetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
        credentials: 'include',
      })
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(`HTTP ${res.status}: ${text}`);
          }

          if (!res.body) {
            throw new Error('No response body');
          }

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let sseBuffer = '';

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // Flush any remaining bytes from the decoder (incomplete UTF-8 at stream end)
              sseBuffer += decoder.decode();
              break;
            }

            sseBuffer += decoder.decode(value, { stream: true });
            const [events, remaining] = parseSseChunk(sseBuffer);
            sseBuffer = remaining;

            for (const evt of events) {
              let data: Record<string, unknown>;
              try {
                data = JSON.parse(evt.data);
              } catch {
                continue;
              }

              switch (evt.event) {
                case 'token':
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, content: m.content + (data.text as string) }
                        : m,
                    ),
                  );
                  break;

                case 'tool_start': {
                  const rawName = (data.tool ?? data.name) as string;
                  const parsed = parseAgentTool(rawName);
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? {
                            ...m,
                            toolCalls: [
                              ...(m.toolCalls ?? []),
                              {
                                name: rawName,
                                status: 'running' as const,
                                agent: parsed?.agent,
                                displayName: parsed?.operation,
                              },
                            ],
                          }
                        : m,
                    ),
                  );
                  break;
                }

                case 'tool_result': {
                  const toolName = (data.tool ?? data.name) as string;
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? {
                            ...m,
                            toolCalls: (m.toolCalls ?? []).map((tc) =>
                              tc.name === toolName && tc.status === 'running'
                                ? { ...tc, status: 'done' as const, summary: data.summary as string | undefined }
                                : tc,
                            ),
                          }
                        : m,
                    ),
                  );
                  break;
                }

                // Agent mode: status updates
                case 'status':
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, statusMessage: data.message as string }
                        : m,
                    ),
                  );
                  break;

                // Agent mode: intermediate thinking
                case 'thinking':
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? { ...m, thinkingText: (m.thinkingText ?? '') + (data.text as string) }
                        : m,
                    ),
                  );
                  break;

                // Agent mode: progress reporting
                case 'progress':
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? {
                            ...m,
                            progress: {
                              step: data.step as number,
                              total: data.total as number,
                              description: data.description as string,
                            },
                          }
                        : m,
                    ),
                  );
                  break;

                case 'navigate':
                  window.dispatchEvent(new CustomEvent('martha:navigate', {
                    detail: { url: data.url, reason: data.reason },
                  }));
                  break;

                case 'done':
                  if (data.session_id) {
                    setSessionId(data.session_id as string);
                  }
                  // Finalize: use response text if provided, keep streamed content otherwise
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId
                        ? {
                            ...m,
                            content: (data.response as string) || m.content,
                            isStreaming: false,
                          }
                        : m,
                    ),
                  );
                  // If done event carries an error, show it
                  if (data.error) {
                    setMessages((prev) =>
                      prev.map((m) =>
                        m.id === assistantId
                          ? { ...m, content: data.error as string, isStreaming: false }
                          : m,
                      ),
                    );
                  }
                  // Refresh session list after successful exchange
                  refreshSessions();
                  break;
              }
            }
          }
        })
        .catch((err: Error) => {
          if (err.name === 'AbortError') return; // Cancelled by user — ignore
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: 'Sorry, something went wrong. Please try again.',
                    isStreaming: false,
                  }
                : m,
            ),
          );
        })
        .finally(() => {
          setIsStreaming(false);
          if (abortRef.current === controller) {
            abortRef.current = null;
          }
          // Drain queued message if present
          const queued = queuedRef.current;
          if (queued) {
            queuedRef.current = null;
            setQueuedMessage(null);
            setTimeout(() => sendMessage(queued), 0);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, refreshSessions],
  );

  return {
    messages,
    isStreaming,
    sendMessage,
    cancelStream,
    sessionId,
    sessions,
    loadSession,
    startNewSession,
    archiveSession,
    refreshSessions,
    isLoadingSessions,
    queuedMessage,
    queueMessage,
    clearQueue,
  };
}
